/**
 * PATH: packages/ui/src/components/settings/NotificationsTab.tsx
 * PURPOSE: Orchestrator for notification preference management across all hubs.
 *
 * WHY: Users need a unified place to control alerts, digests, and in-app badges
 *      without hub-specific duplication.
 *
 * FLOW:
 *   ┌──────────────┐    ┌──────────────┐    ┌───────────────┐
 *   │ Load prefs   │───▶│ Update toggles│──▶│ Save settings │
 *   └──────────────┘    └──────────────┘    └───────────────┘
 *
 * DEPENDENCIES:
 *   - lucide-react: icons
 *   - SettingsApiClient: optional API bridge for persistence
 *   - ./notifications-tab/*: types and presentational sub-components
 */

import { useState, useCallback, useEffect, useMemo } from 'react'
import { Bell, Mail, Smartphone, Loader2, Check } from 'lucide-react'
import { cn } from '../../lib/utils'
import { NotificationSection, SubOption } from './notifications-tab/NotificationPreferenceGroup'
import type { NotificationPreferences, NotificationsTabProps } from './notifications-tab/notifications-tab-types'

// Re-export types so existing consumers are not broken
export type { NotificationPreferences, NotificationsTabProps } from './notifications-tab/notifications-tab-types'

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function NotificationsTab({
  preferences: initialPrefs,
  isLoading = false,
  isSaving = false,
  onSave,
  apiClient,
  // NOTE: apiClient baseURL already includes /api, so endpoint should NOT include /api prefix
  endpoint = '/settings/notifications/',
  storageKey = 'quotech-notifications',
  className,
}: NotificationsTabProps) {
  const defaultPreferences = useMemo<NotificationPreferences>(() => ({
    email: {
      enabled: true,
      jobUpdates: true,
      mentions: true,
      dailyDigest: false,
      weeklyReport: true,
      ...initialPrefs?.email,
    },
    push: {
      enabled: false,
      urgentOnly: true,
      sound: true,
      ...initialPrefs?.push,
    },
    inApp: {
      enabled: true,
      showBadge: true,
      ...initialPrefs?.inApp,
    },
  }), [initialPrefs?.email, initialPrefs?.push, initialPrefs?.inApp])

  const [prefs, setPrefs] = useState<NotificationPreferences>(defaultPreferences)
  const [remoteLoading, setRemoteLoading] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    if (!initialPrefs) return
    setPrefs(prev => ({
      email: { ...prev.email, ...initialPrefs.email },
      push: { ...prev.push, ...initialPrefs.push },
      inApp: { ...prev.inApp, ...initialPrefs.inApp },
    }))
  }, [initialPrefs])

  useEffect(() => {
    if (!apiClient) {
      try {
        const stored = localStorage.getItem(storageKey)
        if (stored) {
          setPrefs(JSON.parse(stored))
        }
      } catch {
        // Ignore storage errors
      }
      return
    }

    let isMounted = true
    setRemoteLoading(true)
    apiClient.get(endpoint)
      .then((response) => {
        if (!isMounted) return
        const data = response?.data ?? response
        const serverPrefs = data?.preferences ?? data
        if (serverPrefs) {
          setPrefs((prev) => ({
            email: { ...prev.email, ...serverPrefs.email },
            push: { ...prev.push, ...serverPrefs.push },
            inApp: { ...prev.inApp, ...serverPrefs.inApp },
          }))
          setHasChanges(false)
        }
      })
      .catch(() => {
        if (!isMounted) return
      })
      .finally(() => {
        if (!isMounted) return
        setRemoteLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [apiClient, endpoint, storageKey])

  const updatePrefs = useCallback((updates: Partial<NotificationPreferences>) => {
    setPrefs(prev => {
      const next = { ...prev }
      if (updates.email) next.email = { ...prev.email, ...updates.email }
      if (updates.push) next.push = { ...prev.push, ...updates.push }
      if (updates.inApp) next.inApp = { ...prev.inApp, ...updates.inApp }
      return next
    })
    setHasChanges(true)
  }, [])

  const handleSave = useCallback(async () => {
    if (onSave) {
      try {
        await onSave(prefs)
        setHasChanges(false)
        return
      } catch (err) {
        if (import.meta.env.DEV) console.error('Failed to save notification preferences:', err)
        return
      }
    }

    if (apiClient) {
      try {
        await apiClient.put(endpoint, { preferences: prefs })
        setHasChanges(false)
        return
      } catch (err) {
        if (import.meta.env.DEV) console.error('Failed to save notification preferences:', err)
        return
      }
    }

    try {
      localStorage.setItem(storageKey, JSON.stringify(prefs))
    } catch {
      // Ignore
    }
    setHasChanges(false)
  }, [prefs, onSave, apiClient, endpoint, storageKey])

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------

  if (isLoading || remoteLoading) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
      </div>
    )
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className={cn('space-y-6', className)}>
      {/* Email Notifications */}
      <NotificationSection
        icon={<Mail className="w-5 h-5" />}
        title="Email Notifications"
        description="Receive updates and alerts via email"
        enabled={prefs.email.enabled}
        onToggle={(enabled) => updatePrefs({ email: { ...prefs.email, enabled } })}
        disabled={isSaving}
      >
        <SubOption
          label="Job Updates"
          description="When jobs are assigned, updated, or completed"
          checked={prefs.email.jobUpdates ?? true}
          onChange={(jobUpdates) => updatePrefs({ email: { ...prefs.email, jobUpdates } })}
          disabled={isSaving}
        />
        <SubOption
          label="Mentions"
          description="When someone mentions you in a comment"
          checked={prefs.email.mentions ?? true}
          onChange={(mentions) => updatePrefs({ email: { ...prefs.email, mentions } })}
          disabled={isSaving}
        />
        <SubOption
          label="Daily Digest"
          description="Summary of activity from the past day"
          checked={prefs.email.dailyDigest ?? false}
          onChange={(dailyDigest) => updatePrefs({ email: { ...prefs.email, dailyDigest } })}
          disabled={isSaving}
        />
        <SubOption
          label="Weekly Report"
          description="Weekly summary with key metrics"
          checked={prefs.email.weeklyReport ?? true}
          onChange={(weeklyReport) => updatePrefs({ email: { ...prefs.email, weeklyReport } })}
          disabled={isSaving}
        />
      </NotificationSection>

      {/* Push Notifications */}
      <NotificationSection
        icon={<Smartphone className="w-5 h-5" />}
        title="Push Notifications"
        description="Receive real-time alerts on your device"
        enabled={prefs.push.enabled}
        onToggle={(enabled) => updatePrefs({ push: { ...prefs.push, enabled } })}
        disabled={isSaving}
      >
        <SubOption
          label="Urgent Only"
          description="Only notify for high-priority items"
          checked={prefs.push.urgentOnly ?? true}
          onChange={(urgentOnly) => updatePrefs({ push: { ...prefs.push, urgentOnly } })}
          disabled={isSaving}
        />
        <SubOption
          label="Sound"
          description="Play a sound with notifications"
          checked={prefs.push.sound ?? true}
          onChange={(sound) => updatePrefs({ push: { ...prefs.push, sound } })}
          disabled={isSaving}
        />
      </NotificationSection>

      {/* In-App Notifications */}
      <NotificationSection
        icon={<Bell className="w-5 h-5" />}
        title="In-App Notifications"
        description="Show notifications within Quotech"
        enabled={prefs.inApp.enabled}
        onToggle={(enabled) => updatePrefs({ inApp: { ...prefs.inApp, enabled } })}
        disabled={isSaving}
      >
        <SubOption
          label="Show Badge"
          description="Display unread count on the bell icon"
          checked={prefs.inApp.showBadge ?? true}
          onChange={(showBadge) => updatePrefs({ inApp: { ...prefs.inApp, showBadge } })}
          disabled={isSaving}
        />
      </NotificationSection>

      {/* Save Button */}
      {hasChanges && (
        <div className="flex items-center gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              'bg-teal-500 text-white hover:bg-teal-600',
              isSaving && 'opacity-60 cursor-not-allowed'
            )}
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            Save Preferences
          </button>
        </div>
      )}
    </div>
  )
}

export default NotificationsTab
